import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Store,
  TrendingUp,
  Package,
  PhoneCall,
  ShieldCheck,
  FileText,
  BarChart3,
  Sparkles,
  Upload,
  Cpu,
  Smartphone,
} from "lucide-react";
import { tLang } from "@/contexts/PortalLanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CDN   = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";
const NAVY  = "#1A1A2E";

export default function RetailersES() {

  // ─── Section data ────────────────────────────────────────────────────────────

  const onboardingSteps = [
    {
      icon: Upload,
      title: tLang("es", "retailers.onboarding.step1.title", "Sube tu catálogo de productos"),
      desc:  tLang("es", "retailers.onboarding.step1.desc",  "Agrega tus chimeneas, parrillas y productos de patio al portal Fyrris. Solo necesitas fotos básicas y especificaciones para empezar."),
    },
    {
      icon: Cpu,
      title: tLang("es", "retailers.onboarding.step2.title", "Nosotros gestionamos el procesamiento AR"),
      desc:  tLang("es", "retailers.onboarding.step2.desc",  "Nuestro equipo construye y optimiza un modelo 3D fotorrealista para cada producto. Sin trabajo técnico de tu parte."),
    },
    {
      icon: Smartphone,
      title: tLang("es", "retailers.onboarding.step3.title", "Los clientes visualizan al instante"),
      desc:  tLang("es", "retailers.onboarding.step3.desc",  "Los compradores colocan tus productos en su hogar con AR — en tu sala de exposición en un iPad o en casa desde su propio dispositivo."),
    },
  ];

  const pricingTailored = tLang("es", "retailers.pricing.tailored", "Precios adaptados a tu tienda");
  const pricingOrderForm = tLang("es", "retailers.pricing.orderFormLine", "Detallados en tu formulario de pedido");

  const pricingPlans = [
    {
      key:      "founding",
      name:     tLang("es", "retailers.pricing.founding.name",    "Minorista fundador"),
      badge:    tLang("es", "retailers.pricing.founding.badge",   "Primera cohorte"),
      subtext:  tLang("es", "retailers.pricing.founding.subtext", "Una primera cohorte limitada de minoristas"),
      features: [
        tLang("es", "retailers.pricing.founding.f1", "Widget de visualización AR (integrable en el sitio web)"),
        tLang("es", "retailers.pricing.founding.f2", "Acceso al catálogo de fabricantes"),
        tLang("es", "retailers.pricing.founding.f3", "Portal de administración básico"),
        tLang("es", "retailers.pricing.founding.f4", "Soporte por correo electrónico"),
        tLang("es", "retailers.pricing.founding.f5", "Tarifa fija durante la vigencia de tu acuerdo"),
        tLang("es", "retailers.pricing.founding.f6", "Acceso anticipado a nuevas funciones"),
        tLang("es", "retailers.pricing.founding.f7", "Línea directa con el fundador"),
      ],
      cta:      tLang("es", "retailers.pricing.founding.cta", "Reservar una demo"),
      ctaHref:  "/es/minoristas/demo",
      dotColor: EMBER,
      ctaColor: EMBER,
      badgeClass: "bg-orange-100 text-orange-700",
    },
    {
      key:      "standard",
      name:     tLang("es", "retailers.pricing.standard.name",    "Estándar"),
      badge:    tLang("es", "retailers.pricing.standard.badge",   "Más popular"),
      subtext:  "",
      features: [
        tLang("es", "retailers.pricing.standard.f1", "Todo lo incluido en Fundador"),
        tLang("es", "retailers.pricing.standard.f2", "Catálogo completo de fabricantes"),
        tLang("es", "retailers.pricing.standard.f3", "Análisis de uso AR (cuando esté disponible)"),
        tLang("es", "retailers.pricing.standard.f4", "Datos de clientes potenciales calificados"),
        tLang("es", "retailers.pricing.standard.f5", "Soporte prioritario"),
      ],
      cta:      tLang("es", "retailers.pricing.standard.cta", "Reservar una demo"),
      ctaHref:  "/es/minoristas/demo",
      dotColor: TEAL,
      ctaColor: TEAL,
      badgeClass: "bg-teal-100 text-teal-700",
    },
    {
      key:      "growth",
      name:     tLang("es", "retailers.pricing.growth.name",    "Crecimiento"),
      badge:    tLang("es", "retailers.pricing.growth.badge",   "Próximamente"),
      subtext:  tLang("es", "retailers.pricing.growth.subtext", "Listo para marca blanca · múltiples ubicaciones"),
      features: [
        tLang("es", "retailers.pricing.growth.f1", "Todo lo incluido en Estándar"),
        tLang("es", "retailers.pricing.growth.f2", "Experiencia de marca blanca / personalizada"),
        tLang("es", "retailers.pricing.growth.f3", "Análisis e informes avanzados"),
        tLang("es", "retailers.pricing.growth.f4", "Soporte para múltiples ubicaciones"),
        tLang("es", "retailers.pricing.growth.f5", "Gestor de cuenta dedicado"),
      ],
      cta:      tLang("es", "retailers.pricing.growth.cta", "Reservar una demo"),
      ctaHref:  "/es/minoristas/demo",
      dotColor: "#9CA3AF",
      ctaColor: "#374151",
      badgeClass: "bg-gray-100 text-gray-500",
    },
  ];

  const faqItems = [
    {
      q: tLang("es", "retailers.faq.q1", "¿Qué productos puedo incorporar?"),
      a: tLang("es", "retailers.faq.a1", "Cualquier producto físico del espacio HPB — chimeneas, estufas de leña, insertos de gas, parrillas BBQ, muebles de patio, cocinas exteriores y más. Si tiene dimensiones, podemos crear un modelo AR."),
    },
    {
      q: tLang("es", "retailers.faq.q2", "¿Cuánto tarda la configuración?"),
      a: tLang("es", "retailers.faq.a2", "La mayoría de los minoristas están en línea en 2 a 3 semanas. Nosotros gestionamos todo el procesamiento 3D. Tú envías tu lista de productos y fotos; nosotros nos encargamos del resto."),
    },
    {
      q: tLang("es", "retailers.faq.q3", "¿Necesito personal técnico?"),
      a: tLang("es", "retailers.faq.a3", "No. El portal Fyrris está diseñado para usuarios no técnicos. Si puedes actualizar una hoja de cálculo, puedes gestionar tu catálogo completo y ubicaciones."),
    },
    {
      q: tLang("es", "retailers.faq.q4", "¿Qué pasa cuando se complete la cohorte fundadora?"),
      a: tLang("es", "retailers.faq.a4", "La cohorte fundadora es un primer grupo limitado. Los minoristas que se unan después también reciben precios adaptados a su tienda, detallados en su formulario de pedido. Cualquier compromiso de tarifa es el que indique tu formulario de pedido."),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="bg-gray-950 text-gray-100">
      <Helmet>
        <title>{tLang("es", "retailers.meta.title", "Fyrris para distribuidores HPB")}</title>
        <meta name="description" content={tLang("es", "retailers.meta.description")} />
        <meta name="keywords" content="distribuidor HPB especializado, software minorista chimeneas, sala exposición muebles patio, tienda parrilla BBQ, plataforma AR, minorista vida exterior" />
        <meta property="og:title" content={tLang("es", "retailers.meta.ogTitle")} />
        <meta property="og:description" content={tLang("es", "retailers.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang("es", "retailers.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang("es", "retailers.meta.ogDescription")} />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-12 pb-20 md:pt-14 md:pb-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        <img
          src={`${CDN}/hero/Retail%20Page%20-%20Hero.png`}
          alt="Sala de exposición de un distribuidor HPB con chimeneas y productos de exterior"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#1A1A2E]/70 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-6"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            <Store className="w-4 h-4" />
            {tLang("es", "retailers.hero.eyebrow", "Para distribuidores HPB")}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white mb-6">
            {tLang("es", "retailers.hero.headline1", "Convierte el interés en ventas en tienda")}
            <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl font-semibold" style={{ color: EMBER }}>
              {tLang("es", "retailers.hero.headline2", "con RA que responde cada duda antes de la visita.")}
            </span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mb-10 leading-relaxed">
            {tLang(
              "es",
              "retailers.hero.subhead",
              "Fyrris ayuda a los compradores a previsualizar chimeneas y productos de patio en casa — luego los guía a tu tienda para comprar. Menos dudas, menos devoluciones, compras más seguras."
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/es/minoristas/demo"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {tLang("es", "retailers.hero.cta", "Reservar una demo")}
              <Sparkles className="w-4 h-4" />
            </Link>
            <Link
              to="/es/minoristas#pricing"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-gray-300 border border-gray-600 transition hover:border-gray-400 hover:text-white"
            >
              {tLang("es", "retailers.hero.ctaSecondary", "Ver precios")}
            </Link>
          </div>
        </div>
      </section>

      {/* ── BENEFITS GRID ────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center">
            Por qué los minoristas eligen Fyrris
          </h2>

          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <TrendingUp className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Más tráfico cualificado</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Los compradores exploran productos en su espacio y luego usan la geolocalización
                para encontrar la tienda más cercana que los vende. Recibes clientes
                que ya saben lo que quieren.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Package className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Menos devoluciones</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Probar en RA antes de comprar establece expectativas correctas sobre tamaño,
                estilo y acabado — clientes más satisfechos y menos sorpresas postventa.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <PhoneCall className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Consultas más rápidas</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Los clientes llegan con capturas y medidas desde la app.
                Tu equipo recomienda el modelo correcto y cierra la venta más rápido.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <BarChart3 className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Insights accionables</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Ve qué productos se previsual más en tu zona y ajusta tu inventario.
                Métricas simples — sin equipo de datos.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <ShieldCheck className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Fiable y privacidad primero</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Infraestructura moderna con configuraciones de privacidad responsables.
                Tus datos de negocio son solo tuyos.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Sparkles className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Incorporación sencilla</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Añade tus ubicaciones, sube tu logo y estás en línea. Nuestro equipo
                puede ayudar con la configuración de productos si es necesario.
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-3">
            <Link
              to="/es/minoristas/demo"
              className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {tLang("es", "retailers.pricing.founding.cta", "Reservar una demo")}
              <Sparkles className="w-4 h-4 shrink-0" />
            </Link>
            <p className="text-sm text-gray-400">
              ¿Ya eres socio?{" "}
              <Link to="/signin" className="hover:underline" style={{ color: TEAL }}>
                Iniciar sesión
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ──────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl bg-gray-900 border border-gray-800 px-8 py-10 md:px-12 md:py-12 relative overflow-hidden">
            <span
              className="absolute top-4 left-6 text-7xl font-serif leading-none select-none pointer-events-none"
              aria-hidden="true"
              style={{ color: EMBER, opacity: 0.25 }}
            >
              &ldquo;
            </span>

            <blockquote className="relative space-y-5">
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed font-medium">
                {tLang(
                  "es",
                  "retailers.testimonial.quote",
                  "Fyrris ha cambiado la forma en que nuestros clientes compran chimeneas. Llegan sabiendo exactamente lo que quieren."
                )}
              </p>
              <footer className="text-sm text-gray-500">
                — {tLang("es", "retailers.testimonial.attribution", "[Nombre del minorista], Tienda especializada HPB")}
              </footer>
            </blockquote>

            <div
              className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-10 pointer-events-none"
              style={{ backgroundColor: EMBER }}
            />
          </div>
        </div>
      </section>

      {/* ── HOW ONBOARDING WORKS ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("es", "retailers.onboarding.title", "Cómo funciona la incorporación")}
          </h2>
          <p className="text-center text-gray-400 mb-14 max-w-xl mx-auto">
            {tLang("es", "retailers.onboarding.subtitle", "Estás en línea en menos de tres semanas. Sin desarrolladores.")}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {onboardingSteps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>
                <Icon className="w-7 h-7" style={{ color: TEAL }} />
                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                {idx < onboardingSteps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${EMBER}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("es", "retailers.pricing.title", "Precios adaptados a tu tienda")}
          </h2>
          <p className="text-center text-gray-400 mb-14 max-w-xl mx-auto">
            {tLang("es", "retailers.pricing.subtitle", "Detallados en tu formulario de pedido, sin sorpresas.")}
          </p>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {pricingPlans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl flex flex-col bg-white shadow-xl overflow-hidden ${
                  plan.key === "founding" ? "md:scale-[1.02] md:z-10" : ""
                }`}
                style={
                  plan.key === "founding"
                    ? { border: `2px solid ${EMBER}` }
                    : { border: "1px solid #E5E7EB" }
                }
              >
                <div className="px-6 pt-6">
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${plan.badgeClass}`}>
                    {plan.badge}
                  </span>
                </div>
                <div className="px-6 pt-3">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                </div>
                {/* 3. Pricing line: no public prices. Tailored to the store, set out in the Order Form. */}
                <div className="px-6 pt-2">
                  <p className="text-lg font-semibold text-gray-900">{pricingTailored}</p>
                  <p className="text-sm text-gray-500">{pricingOrderForm}</p>
                </div>
                {plan.subtext && <p className="px-6 pt-1 text-sm text-gray-500">{plan.subtext}</p>}
                <div className="mx-6 mt-5 border-t border-gray-100" />
                <ul className="px-6 pt-5 flex flex-col gap-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: plan.dotColor }}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>
                <div className="mx-6 mt-4 border-t border-gray-100" />
                <div className="px-6 pt-4 pb-6">
                  <Link
                    to={plan.ctaHref}
                    className="w-full inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90"
                    style={{ backgroundColor: plan.ctaColor }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 mx-auto max-w-md bg-gray-900 rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
            <p className="text-sm text-gray-400">
              {tLang(
                "es",
                "retailers.pricing.orderFormNote",
                "Tu precio se adapta a tu tienda y se detalla en tu formulario de pedido."
              )}
            </p>
          </div>

        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("es", "retailers.faq.title", "Preguntas frecuentes")}
          </h2>
          <p className="text-center text-gray-400 mb-12">
            {tLang("es", "retailers.faq.subtitle", "¿Más preguntas? Escríbenos a support@fyrris.com")}
          </p>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-gray-800"
              >
                <AccordionTrigger className="text-white hover:text-white text-base font-semibold py-5 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center" style={{ paddingBottom: "0.5rem" }}>
            <Link
              to={"/es/minoristas/demo"}
              className="transition hover:opacity-90"
              style={{
                backgroundColor: "#FF6B35",
                color: "white",
                borderRadius: "9999px",
                padding: "0.75rem 2.5rem",
                whiteSpace: "nowrap",
                fontWeight: "500",
                width: "auto",
                display: "inline-block",
                boxShadow: "0 4px 14px rgba(255,107,53,0.3)",
              }}
            >
              {tLang("es", "retailers.faq.cta", "Reservar una demo →")}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
